/**
 * Email reading — ADR-001 §7.3 (`email_threads`, `email_tone`), §7.6 (`proposed_items`), §8.1/§8.5 (tone and the
 * mail health signals), §10.3, §11.10; ADR-003 (every member's own inbox through their own Google consent,
 * read-only, filtered at the source to client email domains; snippets ≤ 300 characters and a tone quote ≤ 200 —
 * NEVER message bodies; connection is mandatory for ops roles; disconnecting deletes the email-derived rows only
 * that member's mailbox surfaced); ADR-004 (`email_tone`, `email_propose`); scope decision: tone detection ON, the
 * reason line visible to the founder and the associate director only, switchable in Settings; the prototype's
 * MAILS / mailTable / mailNotTask / PROPOSED / propReview / propConfirm / propReject (docs/prototype.html
 * L546–554, L1308–1316, L2239–2298, L2368, L2552–2629).
 *
 * The prototype's "track@ops.amplispot.com" copy is superseded (ADR-003 §10): "We read only threads involving a
 * client's email domain, from the inboxes of members who have connected their account. We store the subject, who
 * it was with and a short snippet — never the message body. No open-tracking pixels."
 *
 * Visibility follows the client: managers all; an ops member the clients they are the current SPOC of (others 404).
 * A thread whose domains match more than one client is "unmatched" — visible to managers and to the SPOC of any
 * client it could belong to, until someone assigns it (never guessed — ADR §10.3).
 */
import { z } from 'zod';
import { IsoDate, PageQuery, Timestamp, Uuid, Version, page } from './common.js';
import { ClientRef } from './clients.js';
import { Escalation, Severity } from './requests.js';
import { TONE_LABELS } from './settings.js';
import { DropReason, DroppedItem, Task } from './tasks.js';
import { UserRef } from './users.js';
const Note = z.string().trim().min(1).max(1000);
/** Query flag: `true` / `false`. */
const BoolString = z.enum(['true', 'false']).transform((s) => s === 'true');
const needsNoteForOther = (b, ctx) => {
    if (b.reason_code === 'other' && !b.note) {
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Add a note when the reason is Other' });
    }
};
// ============================================================================ Google connection (ADR-003 §9)
export const GoogleScope = z.enum(['gmail', 'calendar']).meta({ id: 'GoogleScope' });
/**
 * `not_connected` (never granted); `connected`; `needs_reconnect` (the grant died — a password change, revoked at
 * Google, expired); `admin_blocked` (the Workspace administrator's policy refuses it — a different message, not a
 * reconnect prompt: ADR-003 §5.2); `revoked` (disconnected here).
 */
export const GoogleConnectionState = z
    .enum(['not_connected', 'connected', 'needs_reconnect', 'admin_blocked', 'revoked'])
    .meta({ id: 'GoogleConnectionState' });
export const GoogleScopeStatus = z
    .object({
    state: GoogleConnectionState,
    connected_at: Timestamp.nullable(),
    /** The last successful poll or sync of this mailbox / calendar. */
    last_success_at: Timestamp.nullable(),
    /** Plain-English reason when not connected or broken, e.g. "Google stopped accepting the connection after a password change. Connect again." */
    problem: z.string().nullable(),
})
    .meta({ id: 'GoogleScopeStatus' });
/** GET /me/google — never any token material. */
export const MyGoogleConnection = z
    .object({
    gmail: GoogleScopeStatus,
    calendar: GoogleScopeStatus,
    /** What Google actually granted (verified against what was asked — ADR-003 §3.3). */
    granted_scopes: z.array(z.string()),
    /** Ops roles must connect (operator decision): the email and calendar surfaces prompt until they do. */
    required: z.boolean(),
    /** The 90-day history read after the first connect (decision G4). */
    backfill: z.enum(['none', 'running', 'done', 'failed']),
})
    .meta({ id: 'MyGoogleConnection' });
export const GoogleConnectQuery = z
    .object({
    scope: GoogleScope,
    /** Where to land afterwards (a path on this site). Default: Settings → My connections. */
    redirect: z
        .string()
        .max(512)
        .regex(/^\/(?!\/)[^\s\\]*$/, 'redirect must be a path on this site, starting with a single /')
        .optional(),
})
    .strict();
/** Google sends `code`+`state`, or `error`+`state` when the person backs out. */
export const GoogleConnectCallbackQuery = z.object({
    code: z.string().min(1).max(2048).optional(),
    state: z.string().min(1).max(512),
    error: z.string().max(256).optional(),
    scope: z.string().max(2048).optional(),
    hd: z.string().max(256).optional(),
    authuser: z.string().max(16).optional(),
    prompt: z.string().max(64).optional(),
});
/** Failures land on `<redirect>?google_error=<code>`; the SPA prints the matching copy. */
export const GoogleConnectError = z
    .enum(['cancelled', 'scope_not_granted', 'wrong_account', 'state_mismatch', 'admin_blocked', 'google_unavailable'])
    .meta({ id: 'GoogleConnectError' });
export const GOOGLE_CONNECT_ERROR_MESSAGE = {
    cancelled: 'Connecting was cancelled. Press Connect to try again.',
    scope_not_granted: 'Google did not give read access. Press Connect again and leave the box ticked.',
    wrong_account: 'That was a different Google account. Connect with your amplispot.com account.',
    state_mismatch: 'That link expired. Press Connect to start again.',
    admin_blocked: 'Your Google Workspace administrator has not allowed this app yet. Ask them to allow Amplispot Ops.',
    google_unavailable: 'Google did not respond. Wait a minute and try again.',
};
export const GoogleScopeParams = z.object({ scope: GoogleScope });
/** DELETE /me/google/{scope}. Revoking at Google removes BOTH scopes (ADR-003 §5.3), so both are reported. */
export const DisconnectGoogleResponse = z
    .object({
    connection: MyGoogleConnection,
    /** Email-derived rows deleted because only this person's mailbox surfaced them (ADR-003 §5.4). */
    deleted_threads: z.number().int().nonnegative(),
})
    .meta({ id: 'DisconnectGoogleResponse' });
/** GET /integrations/google — the managers' coverage view (ADR-003 §6). No token material, ever. */
export const GoogleCoverageRow = z
    .object({
    user: UserRef,
    gmail: GoogleScopeStatus,
    calendar: GoogleScopeStatus,
    required: z.boolean(),
    /** Hours since sign-up or since the grant broke, while a required connection is missing (24 h banner, 72 h flag). */
    missing_for_hours: z.number().int().nonnegative().nullable(),
    /** Clients this person is SPOC of that have no email signals because of it. */
    clients_without_email_signals: z.number().int().nonnegative(),
})
    .meta({ id: 'GoogleCoverageRow' });
export const GoogleCoverage = z
    .object({
    data: z.array(GoogleCoverageRow),
    /** "N members have not connected email — M clients have no email signals." */
    members_not_connected: z.number().int().nonnegative(),
    clients_without_email_signals: z.number().int().nonnegative(),
    as_of: Timestamp,
})
    .meta({ id: 'GoogleCoverage' });
export const GoogleUserParams = z.object({ user_id: Uuid });
export const ResyncResponse = z.object({ queued: z.boolean(), queued_at: Timestamp }).meta({ id: 'ResyncResponse' });
// ============================================================================ tone
/** ADR §8.5 labels plus `unknown` (the model failed or the reply was unreadable — penalty 0, ADR §12). */
export const ToneLabel = z.enum([...TONE_LABELS, 'unknown']).meta({ id: 'ToneLabel' });
/** Prototype chips (mailTone). */
export const TONE_LABEL_CHIP = {
    leave: 'Hints at leaving',
    escalate: 'Threatens escalation',
    negative: 'Unhappy',
    positive: 'Positive',
    urgent: 'Urgent ask',
    neutral: 'Neutral',
    unknown: 'Tone not read',
};
/**
 * The tone of the latest inbound message — for the founder and the associate director (`toneReason.read`): the
 * label, the model's one-line reason and the ≤ 200-character quote it rests on. The penalty is looked up from
 * Settings, never taken from the model (ADR-004).
 */
export const ToneForManagers = z
    .object({
    label: ToneLabel,
    chip: z.string(),
    /**
     * Health points this tone takes off the score, from settings `tone.penalties` (ADR §8.5): leave 20, escalate 15,
     * negative 8, urgent and neutral 0. The `positive` label's −5 is a bonus — a negative penalty adds points back.
     */
    penalty: z.number().int(),
    reason: z.string(),
    quote: z.string().max(200),
    evaluated_at: Timestamp,
})
    .strict()
    .meta({ id: 'ToneForManagers' });
/** What a member sees, and only while `tone.show_label_to_members` is on: the label, never the reason or the quote. */
export const ToneLabelOnly = z.object({ label: ToneLabel, chip: z.string() }).strict().meta({ id: 'ToneLabelOnly' });
export const ThreadTone = z.union([ToneForManagers, ToneLabelOnly]).meta({ id: 'ThreadTone' });
// ============================================================================ email threads
/** Prototype EMS: `waiting` "Waiting on client" · `needs` "Needs a reply from us" · `replied` "Replied". */
export const EmailThreadState = z.enum(['waiting', 'needs', 'replied']).meta({ id: 'EmailThreadState' });
export const EMAIL_STATE_LABEL = {
    waiting: 'Waiting on client',
    needs: 'Needs a reply from us',
    replied: 'Replied',
};
/** Who wrote last: `in` = the client ("Received"), `out` = us ("Sent"). */
export const EmailDirection = z.enum(['in', 'out']).meta({ id: 'EmailDirection' });
const EmailThreadBase = z
    .object({
    id: Uuid,
    /** `null` while unmatched (its domains match more than one client). */
    client: ClientRef.nullable(),
    /**
     * Unmatched only: the clients it could belong to — for a manager all of them; for an ops member ONLY those they
     * are the SPOC of (ADR-001 §6.2: own clients only), never another client's name, priority or SPOC. Empty once
     * matched. A member sees an unmatched thread only when at least one candidate is theirs.
     */
    candidate_clients: z.array(ClientRef),
    subject: z.string(),
    direction: EmailDirection,
    state: EmailThreadState,
    /** Whole days in the current state (prototype "{label} {days}d"). */
    days_in_state: z.number().int().nonnegative(),
    last_message_at: Timestamp,
    message_count: z.number().int().positive(),
    /** ≤ 300 characters of the latest message. Bodies are never stored. */
    snippet: z.string().max(300),
    participant_domains: z.array(z.string()),
    /** The client's current SPOC (prototype "Owner"). */
    owner: UserRef.nullable(),
    linked_task: z.object({ id: Uuid, title: z.string(), state: z.string() }).nullable(),
    linked_escalation_id: Uuid.nullable(),
    /** Marked "Not a task" (with a reason, logged in Dropped tasks). */
    not_a_task: z.boolean(),
    pending_proposals: z.number().int().nonnegative(),
    /** `true` when the caller's own mailbox surfaced it — the SPA can then offer "Open in Gmail". */
    in_my_mailbox: z.boolean(),
    /** Opens the thread in the caller's Gmail; `null` unless `in_my_mailbox`. */
    gmail_url: z.string().nullable(),
    /** The caller may assign it, mark it, or make a task from it (a manager, or the client's SPOC). */
    can_edit: z.boolean(),
    version: Version,
    updated_at: Timestamp,
});
/**
 * Tone follows the one masking rule (common.ts TONE_REASON_SIGNALS; `toneReason.read` and `revenue.read` belong
 * to the same roles, so these routes use the revenue `byAudience` split). Managers: `ToneForManagers`. Everyone
 * else: `ToneLabelOnly`, and only while `tone.show_label_to_members` is on. `tone` is absent while tone detection
 * is off and for an outbound-only thread. Both variants are strict, so a reason or quote sent to a member fails
 * response validation.
 */
export const EmailThreadManager = EmailThreadBase.extend({ tone: ToneForManagers.optional() }).strict().meta({ id: 'EmailThreadManager' });
export const EmailThreadMember = EmailThreadBase.extend({ tone: ToneLabelOnly.optional() }).strict().meta({ id: 'EmailThreadMember' });
export const EmailThread = z.union([EmailThreadManager, EmailThreadMember]).meta({ id: 'EmailThread' });
export const ListEmailThreadsQuery = PageQuery.extend({
    client_id: Uuid.optional(),
    state: EmailThreadState.optional(),
    direction: EmailDirection.optional(),
    /** `true` = no linked task or escalation and not marked "Not a task". */
    unlinked: BoolString.optional(),
    /** `true` = only threads waiting to be assigned to a client. */
    unmatched: BoolString.optional(),
}).strict();
export const EmailThreadStats = z
    .object({
    tracked: z.number().int().nonnegative(),
    waiting: z.number().int().nonnegative(),
    needs: z.number().int().nonnegative(),
    replied: z.number().int().nonnegative(),
    unmatched: z.number().int().nonnegative(),
})
    .meta({ id: 'EmailThreadStats' });
const EmailThreadPageExtras = {
    stats: EmailThreadStats,
    /** The caller's own Gmail state — the Emails tab shows "Connect your email" until connected (ADR-003 §10). */
    my_gmail: GoogleConnectionState,
    as_of: Timestamp,
    /** The oldest last-successful poll among the mailboxes behind these threads (staleness banner, ADR §11.1). */
    source_last_success_at: Timestamp.nullable(),
};
export const EmailThreadPageManager = page(EmailThreadManager)
    .extend({
    ...EmailThreadPageExtras,
    /** SPOCs of active clients whose mailbox is not connected for reading (managers' "N members not connected"). */
    members_not_connected: z.number().int().nonnegative(),
})
    .meta({ id: 'EmailThreadPageManager' });
/** A member's page: `members_not_connected` is always `null` (a count about colleagues is a manager figure). */
export const EmailThreadPageMember = page(EmailThreadMember)
    .extend({ ...EmailThreadPageExtras, members_not_connected: z.null() })
    .meta({ id: 'EmailThreadPageMember' });
export const EmailThreadPage = z.union([EmailThreadPageManager, EmailThreadPageMember]).meta({ id: 'EmailThreadPage' });
/**
 * PATCH /email-threads/{id}. `client_id` assigns an unmatched thread (one of its `candidate_clients`, else 422) or
 * moves a matched one. Who may set it: a manager, into any candidate (or any client, for a matched thread); an ops
 * member only into a client they are the SPOC of — another client is 403 (`field_errors` on `client_id`); `state` corrects the state by hand; `not_a_task` marks it ("This email needs no task") — a reason
 * is required, the thread becomes `replied`, a Dropped tasks row is logged ("Email") and its pending proposals
 * are rejected with the same reason. `not_a_task: false` undoes the mark.
 */
export const UpdateEmailThreadBody = z
    .object({
    client_id: Uuid.optional(),
    state: EmailThreadState.optional(),
    not_a_task: z
        .union([
        z.literal(false),
        z.object({ reason_code: DropReason, note: Note.optional() }).strict().superRefine(needsNoteForOther),
    ])
        .optional(),
})
    .strict()
    .refine((b) => Object.values(b).some((v) => v !== undefined), { message: 'Change at least one thing' });
const updateThreadResponse = (thread) => z.object({
    thread,
    dropped_item: DroppedItem.nullable(),
    /**
     * After assigning a thread to a client: the participant domains not yet on that client — the SPA offers to add
     * them through `POST /clients/{id}/email-domains` (managers), so the next thread matches by itself.
     */
    domain_offer: z.array(z.string()),
});
export const UpdateEmailThreadResponseManager = updateThreadResponse(EmailThreadManager).meta({ id: 'UpdateEmailThreadResponseManager' });
export const UpdateEmailThreadResponseMember = updateThreadResponse(EmailThreadMember).meta({ id: 'UpdateEmailThreadResponseMember' });
export const UpdateEmailThreadResponse = z
    .union([UpdateEmailThreadResponseManager, UpdateEmailThreadResponseMember])
    .meta({ id: 'UpdateEmailThreadResponse' });
/**
 * POST /email-threads/{id}/task — the prototype's "Make task" on a thread with no proposal to confirm. Makes a
 * task (source `email`) for the client's SPOC — or an escalation with `kind: 'esc'` — and links it to the thread.
 * A matched thread only (`409` while unmatched); one link per thread (`409` once linked).
 */
export const MakeThreadTaskBody = z
    .object({
    kind: z.enum(['task', 'esc']).default('task'),
    title: z.string().trim().min(3, 'Say what has to be done in a few words').max(200),
    due: IsoDate.optional(),
    /** Someone other than the SPOC needs `task.reassign`. */
    assignee_id: Uuid.optional(),
    severity: Severity.optional(),
})
    .strict();
/** The made item and the thread after it. Exactly one of `task` / `escalation` is set. */
const madeFromEmail = (thread) => z.object({ task: Task.nullable(), escalation: Escalation.nullable(), thread });
export const MadeFromEmailManager = madeFromEmail(EmailThreadManager).meta({ id: 'MadeFromEmailManager' });
export const MadeFromEmailMember = madeFromEmail(EmailThreadMember).meta({ id: 'MadeFromEmailMember' });
export const MadeFromEmail = z.union([MadeFromEmailManager, MadeFromEmailMember]).meta({ id: 'MadeFromEmail' });
// ============================================================================ proposed items
/** `pending` → `confirmed` (became a task or an escalation) or `rejected` (with a reason, in Dropped tasks). */
export const ProposedStatus = z.enum(['pending', 'confirmed', 'rejected']).meta({ id: 'ProposedStatus' });
/** Prototype LATE_H: a proposal not answered for 4 hours reaches a manager (ADR §7.6, §9 `held.age_check`). */
export const PROPOSED_LATE_HOURS = 4;
export const ProposedItem = z
    .object({
    id: Uuid,
    source: z.object({
        kind: z.literal('email'),
        thread_id: Uuid,
        subject: z.string(),
        direction: EmailDirection,
        at: Timestamp,
    }),
    client: ClientRef,
    /** `task`, or `esc` — "Will be raised as an escalation". */
    kind: z.enum(['task', 'esc']),
    title: z.string(),
    /** The sentence it came from, ≤ 200 characters. */
    quote: z.string().max(200),
    /** Proposals go to the client's current SPOC first. */
    suggested_assignee: UserRef.nullable(),
    suggested_due: IsoDate.nullable(),
    /** From our own outbound mail ("we will send…") — a promise we made. */
    promise: z.boolean(),
    proposed_at: Timestamp,
    /** Whole hours since it was proposed (prototype "Proposed Nh ago" / "Waiting Nh"). */
    waited_hours: z.number().int().nonnegative(),
    /** Pending for `PROPOSED_LATE_HOURS` or more — shown to managers. */
    is_late: z.boolean(),
    status: ProposedStatus,
    created_task_id: Uuid.nullable(),
    created_escalation_id: Uuid.nullable(),
    dropped_item_id: Uuid.nullable(),
    decided_by: UserRef.nullable(),
    decided_at: Timestamp.nullable(),
    /** The caller may confirm or reject it (the suggested assignee, the client's SPOC, or a manager). */
    can_decide: z.boolean(),
    version: Version,
})
    .meta({ id: 'ProposedItem' });
export const ListProposedItemsQuery = PageQuery.extend({
    status: ProposedStatus.optional(),
    /** `true` = pending for 4 hours or more. */
    late: BoolString.optional(),
    client_id: Uuid.optional(),
    thread_id: Uuid.optional(),
    /** Proposed on this day (IST) — the managers' "Created today". */
    on: IsoDate.optional(),
}).strict();
export const ProposedItemPage = page(ProposedItem)
    .extend({ stats: z.object({ pending: z.number().int().nonnegative(), late: z.number().int().nonnegative() }) })
    .meta({ id: 'ProposedItemPage' });
/**
 * POST /proposed-items/{id}/confirm. Edits are optional. `kind` may switch task ↔ escalation. Only `task.reassign`
 * holders pick someone other than the client's SPOC (prototype: the head only). Proposals from client mail never
 * become `urgent` by themselves (ADR-003 §4.4): a task due today is `high`, otherwise `medium`.
 * `409` once decided.
 */
export const ConfirmProposedBody = z
    .object({
    title: z.string().trim().min(3, 'Add a title').max(200).optional(),
    due: IsoDate.optional(),
    assignee_id: Uuid.optional(),
    kind: z.enum(['task', 'esc']).optional(),
    /** Escalations only; default `med` (as the prototype). */
    severity: Severity.optional(),
})
    .strict();
/** Exactly one of `task` / `escalation` is set. */
export const ConfirmProposedResponse = z
    .object({ proposed_item: ProposedItem, task: Task.nullable(), escalation: Escalation.nullable() })
    .meta({ id: 'ConfirmProposedResponse' });
/** POST /proposed-items/{id}/reject — "Rejected and logged in Dropped tasks." `409` once decided. */
export const RejectProposedBody = z.object({ reason_code: DropReason, note: Note.optional() }).strict().superRefine(needsNoteForOther);
export const RejectProposedResponse = z
    .object({ proposed_item: ProposedItem, dropped_item: DroppedItem })
    .meta({ id: 'RejectProposedResponse' });
//# sourceMappingURL=mail.js.map